package com.trevorwiebe.timesheet.admin.organization.presentation

import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun OrganizationScreen(
    viewModel: OrganizationViewModel = koinViewModel(),
    onBack: () -> Unit,
) {
    val state by viewModel.state.collectAsState()

    var showDatePicker by remember { mutableStateOf(false) }

    // Pop back once the save succeeds.
    LaunchedEffect(state.isSaved) {
        if (state.isSaved) {
            viewModel.onEvent(OrganizationEvent.OnSavedHandled)
            onBack()
        }
    }

    NativeDatePicker(
        date = state.goLiveDate,
        showDatePicker = showDatePicker,
        onDismiss = { showDatePicker = false },
        onDateSelected = {
            viewModel.onEvent(OrganizationEvent.OnGoLiveDateChanged(it))
            showDatePicker = false
        }
    )

    Scaffold(
        topBar = {
            TopBar(
                title = "Organization",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        if (state.isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
            }
        } else {
            Column(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .fillMaxSize()
                    .padding(16.dp)
            ) {
                TimesheetTextField(
                    text = state.name,
                    label = "Organization Name",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { viewModel.onEvent(OrganizationEvent.OnNameChanged(it)) }
                )

                Spacer(modifier = Modifier.height(8.dp))

                // Go-live date — a read-only field with a transparent clickable overlay
                // that opens the native date picker.
                Box {
                    TimesheetTextField(
                        text = Util.toFriendlyDate(state.goLiveDate),
                        label = "Go Live Date",
                        readOnly = true
                    )
                    Box(
                        modifier = Modifier
                            .matchParentSize()
                            .clickable { showDatePicker = true }
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))
                Text(
                    text = "Invoice header",
                    style = MaterialTheme.typography.titleSmall,
                    color = MaterialTheme.colorScheme.tertiary,
                )
                Text(
                    text = "Printed at the top of every invoice PDF.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(modifier = Modifier.height(8.dp))
                TimesheetTextField(
                    text = state.billingAddress,
                    label = "Billing address",
                    onTextChange = { viewModel.onEvent(OrganizationEvent.OnBillingAddressChanged(it)) }
                )
                Spacer(modifier = Modifier.height(8.dp))
                TimesheetTextField(
                    text = state.billingPhone,
                    label = "Phone",
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    onTextChange = { viewModel.onEvent(OrganizationEvent.OnBillingPhoneChanged(it)) }
                )
                Spacer(modifier = Modifier.height(8.dp))
                TimesheetTextField(
                    text = state.billingEmail,
                    label = "Billing email",
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                    onTextChange = { viewModel.onEvent(OrganizationEvent.OnBillingEmailChanged(it)) }
                )
                Spacer(modifier = Modifier.height(8.dp))
                TimesheetTextField(
                    text = state.remitTo,
                    label = "Remit to / payment note",
                    onTextChange = { viewModel.onEvent(OrganizationEvent.OnRemitToChanged(it)) }
                )

                Spacer(modifier = Modifier.height(24.dp))

                Button(
                    onClick = { viewModel.onEvent(OrganizationEvent.OnSave) },
                    enabled = state.name.isNotBlank() && !state.isSaving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.tertiary,
                        contentColor = MaterialTheme.colorScheme.onTertiary
                    )
                ) {
                    if (state.isSaving) {
                        CircularProgressIndicator(
                            modifier = Modifier.height(20.dp),
                            color = MaterialTheme.colorScheme.onTertiary
                        )
                    } else {
                        Text("Save")
                    }
                }
            }
        }

        NativeDialog(
            visible = state.error != null,
            title = "Something went wrong",
            message = state.error ?: "",
            confirmText = "OK",
            onConfirm = { viewModel.onEvent(OrganizationEvent.OnErrorShown) },
            onDismiss = { viewModel.onEvent(OrganizationEvent.OnErrorShown) }
        )
        }
    }
}
